"use client";

import { useEffect, useRef } from "react";
import CountUp from "@/components/ui/CountUp";


const dataPoints = [
  {
    stat: 80000,
    prefix: "~",
    suffix: " t",
    label: "Global Annual Consumption",
    detail:
      "Driven by flame retardants, lead-acid batteries, and PET catalyst demand.",
  },
  {
    stat: 63,
    suffix: "%",
    label: "China's Share of Primary Output",
    detail:
      "Supply concentration risk is accelerating demand for non-Chinese sourcing.",
  },
  {
    stat: 4.8,
    suffix: "% CAGR",
    decimals: 1,
    label: "Projected Demand Growth 2024–2030",
    detail:
      "Battery storage, EV production, and semiconductor applications driving growth.",
  },
];

export default function WhyAntimonySection() {
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const els = ref?.current?.querySelectorAll(".reveal");
        if (!els) return;
        const observer = new IntersectionObserver(
            (entries) =>
                entries.forEach((e) => {
                    if (e.isIntersecting) {
                        e.target.classList.add("visible");
                        observer.unobserve(e.target);
                    }
                }),
            { threshold: 0.1 }
        );
        els?.forEach((el) => observer?.observe(el));
        return () => observer?.disconnect();
    }, []);

    return (
        <section
            ref={ref}
            className="section-navy"
            style={{ padding: "120px 0" }}
            aria-labelledby="why-antimony-heading"
        >
            <div
                className="px-6 lg:px-12"
                style={{ maxWidth: "1280px", margin: "0 auto" }}
            >
                {/* Header row */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start mb-20">
                    <div>
                        <span className="type-subheading reveal">Why Antimony Matters</span>
                        <h2
                            id="why-antimony-heading"
                            className="reveal reveal-delay-1"
                            style={{
                                fontFamily: "var(--font-serif)",
                                fontSize: "clamp(2rem, 3.5vw, 3rem)",
                                fontWeight: 400,
                                color: "var(--color-white)",
                                lineHeight: 1.1,
                                letterSpacing: "-0.02em",
                                marginTop: "12px",
                            }}
                        >
                            A critical mineral
                            <br />
                            under structural
                            <br />
                            supply pressure.
                        </h2>
                    </div>
                    <div className="flex flex-col gap-6 justify-center">
                        <p
                            className="reveal reveal-delay-2"
                            style={{
                                fontFamily: "var(--font-sans)",
                                fontSize: "1.0625rem",
                                color: "rgba(255,255,255,0.62)",
                                lineHeight: "1.7",
                            }}
                        >
                            Antimony is classified as a critical raw material by the EU, US,
                            and UK. As Chinese export restrictions tighten, refiners and
                            industrial processors are actively diversifying their supply
                            chains to non-Chinese, compliant sources.
                        </p>
                        <p
                            className="reveal reveal-delay-2"
                            style={{
                                fontFamily: "var(--font-sans)",
                                fontSize: "0.9375rem",
                                color: "rgba(255,255,255,0.45)",
                                lineHeight: "1.65",
                            }}
                        >
                            GFS Service Ltd. provides direct access to primary production
                            from Tajikistan and South Africa — geopolitically stable, fully
                            documented, and aligned to Western regulatory requirements.
                        </p>
                    </div>
                </div>

                {/* Stat cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-20">
                {dataPoints?.map((dp, i) => (
                    <div
                    key={dp?.label}
                    className={`reveal reveal-delay-${i + 1}`}
                    style={{
                        background: "rgba(255,255,255,0.04)",
                        border: "1px solid rgba(255,255,255,0.08)",
                        borderRadius: "4px",
                        padding: "36px 32px",
                    }}
                    >
                    <p
                        style={{
                        fontFamily: "var(--font-serif)",
                        fontSize: "2.5rem",
                        color: "var(--color-white)",
                        lineHeight: 1,
                        marginBottom: "10px",
                        }}
                    >
                        {typeof dp?.stat === "number" ? (
                        <CountUp
                            end={dp.stat}
                            decimals={dp.decimals}
                            suffix={dp.suffix}
                        />
                        ) : (
                        dp?.stat
                        )}
                    </p>

                    <p
                        style={{
                        fontFamily: "var(--font-sans)",
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        letterSpacing: "0.1em",
                        textTransform: "uppercase",
                        color: "rgba(255,255,255,0.55)",
                        marginBottom: "12px",
                        }}
                    >
                        {dp?.label}
                    </p>

                    <p
                        style={{
                        fontFamily: "var(--font-sans)",
                        fontSize: "0.875rem",
                        color: "rgba(255,255,255,0.38)",
                        lineHeight: "1.6",
                        }}
                    >
                        {dp?.detail}
                    </p>
                    </div>
                ))}
                </div>

                {/* Application grid */}
                <div
                    className="reveal"
                    style={{
                        borderTop: "1px solid rgba(255,255,255,0.08)",
                        paddingTop: "40px",
                    }}
                >
                    <p
                        style={{
                            fontFamily: "var(--font-sans)",
                            fontSize: "0.75rem",
                            fontWeight: 600,
                            letterSpacing: "0.1em",
                            textTransform: "uppercase",
                            color: "rgba(255,255,255,0.3)",
                            marginBottom: "20px",
                        }}
                    >
                        Key End-Use Applications
                    </p>
                    <div className="flex flex-wrap gap-3">
                        {[
                            "Lead-Acid Battery Alloys",
                            "Flame-Retardant Compounds",
                            "PET Catalyst",
                            "Semiconductor Dopants",
                            "Bearing Metals",
                            "Military Munitions",
                            "Glass Decolorisation",
                            "Plastics Stabilisers",
                        ]?.map((app) => (
                            <span
                                key={app}
                                style={{
                                    fontFamily: "var(--font-sans)",
                                    fontSize: "0.8125rem",
                                    color: "rgba(255,255,255,0.55)",
                                    background: "rgba(255,255,255,0.04)",
                                    border: "1px solid rgba(255,255,255,0.1)",
                                    padding: "8px 16px",
                                    borderRadius: "2px",
                                }}
                            >
                                {app}
                            </span>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}